import { useState, FormEvent } from 'react';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const milestones = [
  { year: '2012', title: '品牌创立', desc: '喷水系统专业方案平台正式成立，专注于喷水系统技术资讯与方案服务。' },
  { year: '2014', title: '技术团队组建', desc: '汇聚农业灌溉、景观设计和工业工程领域资深专家，组建专业技术顾问团队。' },
  { year: '2016', title: '服务覆盖全国', desc: '喷水系统咨询服务覆盖全国30余省市，累计服务客户超过5000家。' },
  { year: '2019', title: '智能喷水平台', desc: '率先推出智能喷水系统技术方案，引领行业数字化转型。' },
  { year: '2022', title: '十年里程碑', desc: '成立十周年，成为喷水系统领域最具影响力的专业平台之一。' },
  { year: '2025', title: '持续创新', desc: '深耕喷水技术前沿，推动精准灌溉和智能水景技术普及应用。' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus(null);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotVal = (formData.get('company_alt') as string || '').trim();
    if (honeypotVal) {
      setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快与您联系！' });
      setSubmitting(false);
      form.reset();
      return;
    }

    formData.delete('company_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9sqhodlbrt1qcgaj8cg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* keep raw */ }

      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快与您联系！' });
        form.reset();
      } else {
        const serverMsg = (parsed as { meta?: { message?: string }; message?: string }).meta?.message
          || (parsed as { message?: string }).message
          || responseText;
        setFormStatus({ type: 'error', message: serverMsg || '提交失败，请稍后再试。' });
      }
    } catch {
      setFormStatus({ type: 'error', message: '网络错误，请稍后重试。' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOMeta
        title="关于我们 - 品牌介绍"
        description="喷水系统专业解决方案平台，专注喷水系统领域十余年。提供农业灌溉、景观喷水、工业冷却等全方位喷水方案，汇聚行业顶尖专家，服务覆盖全国。"
        keywords="喷水系统,关于我们,品牌介绍,喷水方案,联系我们"
        canonicalUrl="https://www.penshuixitong.cn/about"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于喷水系统专业解决方案',
            description: '喷水系统专业解决方案平台，专注喷水系统领域十余年。',
            publisher: {
              '@type': 'Organization',
              name: '喷水系统专业解决方案',
              url: 'https://www.penshuixitong.cn/',
            },
          }),
        }}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 pt-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="py-8">
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-3">
                关于我们
              </h1>
              <p className="text-sm md:text-base text-foreground-600">
                专业喷水系统服务，值得信赖的行业伙伴
              </p>
            </div>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-16 px-4 md:px-6 bg-background-50" aria-labelledby="brand-title">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="lg:w-1/2">
                <h2 id="brand-title" className="font-heading text-2xl font-semibold text-foreground-900 mb-5">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    <strong>喷水系统专业解决方案</strong>成立于2012年，是一家专注于喷水系统领域技术资讯与方案服务的专业平台。
                    我们深耕喷水行业十余年，始终秉持"专业、可靠、高效"的服务理念，致力于为农业、景观和工业领域客户提供最优质的喷水系统方案。
                  </p>
                  <p>
                    我们的团队汇聚了来自农业灌溉、景观设计和工业工程等多个领域的资深专家，凭借深厚的行业积累和丰富的项目经验，
                    已为超过5000家客户提供了从方案咨询、设备选型到安装指导和售后维护的全流程喷水系统服务。
                  </p>
                  <p>
                    作为喷水系统领域的权威平台，我们持续关注喷水技术的最新发展动态，包括智能喷水、精准灌溉、物联网水管理等前沿方向，
                    致力于推动整个喷水行业的技术进步和可持续发展。
                  </p>
                </div>
              </div>
              <div className="lg:w-1/2 w-full h-72 md:h-80 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20professional%20office%20interior%20with%20green%20plants%20and%20natural%20wood%20elements%2C%20warm%20lighting%2C%20team%20collaboration%20space%2C%20clean%20minimal%20design%2C%20professional%20corporate%20photography%2C%20bright%20airy%20atmosphere%2C%20soft%20natural%20tones&width=800&height=500&seq=about-brand-01&orientation=landscape"
                  alt="喷水系统专业解决方案团队"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-16 px-4 md:px-6 bg-background-100/80" aria-labelledby="milestones-title">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 id="milestones-title" className="font-heading text-2xl font-semibold text-foreground-900 mb-3">
                发展历程
              </h2>
              <p className="text-sm text-foreground-600">十余年深耕，每一步都坚实有力</p>
            </div>
            <div className="relative">
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-background-300/60 transform -translate-x-1/2"></div>
              <div className="space-y-8">
                {milestones.map((item, index) => (
                  <div key={index} className={`flex flex-col md:flex-row items-center gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                        <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-2">
                          {item.year}
                        </span>
                        <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">{item.title}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                    <div className="hidden md:flex w-10 h-10 rounded-full bg-accent-500 items-center justify-center flex-shrink-0 relative z-10">
                      <div className="w-3 h-3 rounded-full bg-background-50"></div>
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 px-4 md:px-6 bg-background-50" aria-labelledby="contact-title">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 id="contact-title" className="font-heading text-2xl font-semibold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600">期待与您沟通，为您提供最专业的喷水系统方案</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-10">
              {/* Contact Info */}
              <div className="lg:w-2/5">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">电话咨询</h3>
                      <p className="text-sm text-foreground-600">400-888-9999</p>
                      <p className="text-xs text-foreground-500">周一至周五 9:00-18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">电子邮件</h3>
                      <p className="text-sm text-foreground-600">info@penshuixitong.cn</p>
                      <p className="text-xs text-foreground-500">24小时内回复</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">办公地址</h3>
                      <p className="text-sm text-foreground-600">北京市海淀区中关村科技园</p>
                      <p className="text-xs text-foreground-500">欢迎预约来访</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-wechat-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">微信咨询</h3>
                      <p className="text-sm text-foreground-600">penshuixitong</p>
                      <p className="text-xs text-foreground-500">扫码或搜索添加</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="lg:w-3/5">
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="bg-background-100/80 border border-background-200/70 rounded-lg p-6 space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        姓名 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-3 py-2 text-sm rounded-md border border-background-300/60 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        邮箱 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-3 py-2 text-sm rounded-md border border-background-300/60 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      电话
                    </label>
                    <input
                      type="tel"
                      id="contact-phone"
                      name="phone"
                      className="w-full px-3 py-2 text-sm rounded-md border border-background-300/60 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的联系电话"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      咨询主题 <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="contact-subject"
                      name="subject"
                      required
                      className="w-full px-3 py-2 text-sm rounded-md border border-background-300/60 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    >
                      <option value="">请选择咨询主题</option>
                      <option value="农业灌溉">农业喷水灌溉</option>
                      <option value="景观喷水">景观喷水装置</option>
                      <option value="工业冷却">工业喷水冷却</option>
                      <option value="维护保养">喷水系统维护保养</option>
                      <option value="其他">其他咨询</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      留言内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2 text-sm rounded-md border border-background-300/60 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请描述您的喷水系统需求（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                  </div>

                  {/* Honeypot */}
                  <div className="honeypot-field">
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  {formStatus && (
                    <div className={`text-sm p-3 rounded-md ${formStatus.type === 'success' ? 'bg-primary-100 text-primary-700' : 'bg-red-50 text-red-700'}`}>
                      {formStatus.message}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="whitespace-nowrap cursor-pointer w-full px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? '提交中...' : '提交咨询'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}